import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/common/ConfirmModal';
import {
  Users,
  FolderOpen,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader,
  CheckCircle,
  AlertCircle,
  Shield,
  User as UserIcon
} from 'lucide-react';

function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [selectedUserForProjects, setSelectedUserForProjects] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'USER' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, projectsRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getProjects()
      ]);
      setUsers(usersRes.data.users || []);
      setProjects(projectsRes.data.projects || []);
    } catch (error) {
      showToast('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 1500);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'USER' });
    setModalOpen(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setFormData({ username: u.username, password: '', role: u.role });
    setModalOpen(true);
  };

  const openProjectsModal = async (u) => {
    try {
      const res = await adminAPI.getUser(u.id);
      const userProjects = res.data.projects || [];
      setSelectedUserForProjects(u);
      setSelectedProjects(userProjects.map(p => p.id));
      setProjectsModalOpen(true);
    } catch (error) {
      showToast('Error al cargar proyectos del usuario', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingUser) {
        const updateData = { username: formData.username, role: formData.role };
        if (formData.password) updateData.password = formData.password;
        await adminAPI.updateUser(editingUser.id, updateData);
        showToast('Usuario actualizado correctamente');
      } else {
        await adminAPI.createUser(formData);
        showToast('Usuario creado correctamente');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al guardar usuario', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (u) => {
    setDeleteModal({ open: true, user: u });
  };

  const confirmDelete = async () => {
    const u = deleteModal.user;
    setDeleteModal({ open: false, user: null });

    try {
      await adminAPI.deleteUser(u.id);
      showToast('Usuario eliminado correctamente');
      fetchData();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al eliminar usuario', 'error');
    }
  };

  const handleProjectsSubmit = async () => {
    setSaving(true);
    try {
      await adminAPI.assignProjects(selectedUserForProjects.id, selectedProjects);
      showToast('Proyectos asignados correctamente');
      setProjectsModalOpen(false);
      fetchData(); // Actualizar la tabla
    } catch (error) {
      showToast('Error al asignar proyectos', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleProject = (projectId) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  return (
    <AdminLayout title={<h1>Gestión de Usuarios</h1>}>
      {loading ? (
        <div className="admin-loading">
          <Loader size={32} className="loading-spinner" />
        </div>
      ) : (
        <div className="admin-table-container">
          <div className="table-header">
            <button className="btn-primary" onClick={openCreateModal}>
              <Plus size={20} />
              Nuevo Usuario
            </button>
          </div>
          <table className="admin-table users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Proyectos Asignados</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div className="user-cell">
                      <div className={`user-avatar ${u.role.toLowerCase()}`}>
                        {u.role === 'ADMIN' ? <Shield size={16} /> : <UserIcon size={16} />}
                      </div>
                      <div className="user-info-cell">
                        <span className="user-name">{u.username}</span>
                        <span className="user-date">Creado: {new Date(u.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${u.role.toLowerCase()}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>
                    <div className="projects-badges">
                      {u.projects && u.projects.length > 0 ? (
                        u.projects.map(p => (
                          <span key={p.id} className="project-badge">{p.name}</span>
                        ))
                      ) : (
                        <span className="no-projects">Sin proyectos</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="table-actions-new">
                      <button
                        className="action-btn projects"
                        onClick={() => openProjectsModal(u)}
                      >
                        <FolderOpen size={14} />
                        Proyectos
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={() => openEditModal(u)}
                        disabled={u.username === 'admin'}
                        title={u.username === 'admin' ? 'El usuario admin no se puede editar' : 'Editar'}
                      >
                        <Edit2 size={14} />
                        Editar
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(u)}
                        disabled={u.id === user?.id || u.username === 'admin'}
                        title={u.username === 'admin' ? 'El usuario admin no se puede eliminar' : 'Eliminar'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit User Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Usuario</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contraseña {editingUser && '(dejar vacío para mantener)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Rol</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="USER">USER</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <Loader size={16} className="loading-spinner" /> : null}
                  {editingUser ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Projects Modal */}
      {projectsModalOpen && (
        <div className="modal-overlay" onClick={() => !saving && setProjectsModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FolderOpen size={20} />
                Asignar Proyectos a {selectedUserForProjects?.username}
              </h2>
              <button className="modal-close" onClick={() => setProjectsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="projects-checklist">
              {projects.length === 0 ? (
                <p>No hay proyectos disponibles</p>
              ) : (
                projects.map(p => (
                  <label key={p.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(p.id)}
                      onChange={() => toggleProject(p.id)}
                    />
                    <span>{p.name}</span>
                  </label>
                ))
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setProjectsModalOpen(false)}
              >
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleProjectsSubmit} disabled={saving}>
                {saving ? <Loader size={16} className="loading-spinner" /> : null}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.open}
        title="Eliminar Usuario"
        message={`¿Estás seguro de eliminar al usuario "${deleteModal.user?.username}"? Esta acción no se puede deshacer.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ open: false, user: null })}
      />

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}

export default UsersPage;
